import { useEffect, useRef } from 'react';

export default function HeroSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const section = sectionRef.current;
    if (section) {
      const elements = section.querySelectorAll('.animate-on-scroll');
      elements.forEach((el) => observer.observe(el));
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (bannerRef.current) {
        const scrollY = window.scrollY;
        const rate = scrollY * 0.4;
        bannerRef.current.style.transform = `translateY(${rate}px) scale(1.1)`;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Parallax Banner Background */}
      <div
        ref={bannerRef}
        className="absolute inset-0 w-full h-full transition-transform will-change-transform"
        style={{ transform: 'translateY(0) scale(1.1)' }}
      >
        <img
          src="https://static.readdy.ai/image/ff63a95a3d068db59710e28833339873/723099036efa639eaf64bbaeae3a3f0c.png"
          alt="TNT Coach AI Web Banner"
          className="w-full h-full object-cover object-center"
        />
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-midnight/50 via-midnight/40 to-midnight/60" />
        <div className="absolute inset-0 bg-gradient-to-r from-midnight/30 via-transparent to-midnight/30" />
      </div>

      {/* Ambient glows */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-champagne-gold/5 blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full bg-metallic-gold/5 blur-3xl pointer-events-none" />

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 py-24 pt-32">
        <div className="flex flex-col items-center text-center max-w-4xl mx-auto">
          <div className="animate-on-scroll opacity-0 mb-6 flex justify-center">
            <div className="bg-[#FAF9F6] w-64 h-64 md:w-80 md:h-80 rounded-full flex items-center justify-center shadow-lg shadow-black/10 overflow-hidden">
              <img
                src="https://static.readdy.ai/image/ff63a95a3d068db59710e28833339873/c8acd82f6772edcd77419f513489db94.png"
                alt="TNT Coach AI Logo"
                className="w-full h-full object-contain p-4 md:p-6"
              />
            </div>
          </div>

          <div className="animate-on-scroll opacity-0 mb-6 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-champagne-gold/30 bg-champagne-gold/10 backdrop-blur-sm drop-shadow-md">
            <i className="ri-sparkling-line text-champagne-gold text-sm" />
            <span className="text-champagne-gold text-sm font-medium">AI-Powered Coaching</span>
          </div>

          <h1 className="animate-on-scroll opacity-0 font-heading text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-warm-white leading-tight mb-6 drop-shadow-lg">
            Top Notch{' '}
            <span className="gold-gradient-text">Transformation</span>
            <br />
            Coaching
          </h1>

          <p className="animate-on-scroll opacity-0 text-warm-white/90 text-base md:text-lg leading-relaxed mb-8 max-w-2xl mx-auto drop-shadow-md">
            TNT Coach AI gives you instant coaching guidance whenever you feel stuck, uncertain,
            unmotivated, or ready for your next breakthrough. Choose your coaching style, ask your question,
            and get clarity, confidence, and one practical next step.
          </p>

          <div className="animate-on-scroll opacity-0 flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://tnt-coach-ai.base44.app"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full gold-gradient text-midnight font-bold text-base hover:opacity-90 transition-all duration-300 shadow-lg shadow-champagne-gold/20 whitespace-nowrap"
            >
              <i className="ri-play-circle-line" />
              Start Your First Coaching Session
            </a>
            <a
              href="#how-it-works"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-warm-white/30 text-warm-white font-semibold text-base hover:bg-warm-white/10 transition-all duration-300 backdrop-blur-sm whitespace-nowrap"
            >
              <i className="ri-eye-line" />
              See How It Works
            </a>
          </div>

          <div className="animate-on-scroll opacity-0 mt-8 flex items-center gap-6 justify-center">
            <div className="flex -space-x-2">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="w-9 h-9 rounded-full border-2 border-midnight/80 bg-gradient-to-br from-royal-navy to-midnight flex items-center justify-center"
                >
                  <i className={`ri-${i === 1 ? 'emotion-happy' : i === 2 ? 'heart' : i === 3 ? 'star' : 'user'}-line text-champagne-gold text-xs`} />
                </div>
              ))}
            </div>
            <div className="text-left">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <i key={i} className="ri-star-fill text-champagne-gold text-xs" />
                ))}
              </div>
              <p className="text-warm-white/70 text-xs mt-1">Trusted by 10,000+ users</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
        <a href="#problem" className="flex flex-col items-center gap-2 text-warm-white/50 hover:text-champagne-gold transition-colors">
          <span className="text-xs tracking-widest uppercase">Scroll</span>
          <i className="ri-arrow-down-line text-xl" />
        </a>
      </div>
    </section>
  );
}